import {
  fold, promotionCanCompile, retract, uniqueArgmax,
  type SignalEvent, type TastePriors,
} from '@morph/generation';
import type { Role } from '@morph/spec';
import type { Extent } from '@morph/spec';

/**
 * Field widths, as PRIVATE VIEW STATE — decision 0014, and decision 0036's
 * fourth re-entry condition: *"literal resize remains private view state and
 * restores independently of whether repeated evidence crosses the taste
 * threshold."*
 *
 * A separate key from the ledger on purpose. The ledger is evidence about the
 * person that may one day be pooled; this is where one device happens to have
 * dragged something, and it must never travel.
 */
export const FIELD_WIDTH_KEY = 'morph.demo.field-width.v1';

export function readFieldWidths(storage: ManualTasteStorage): Partial<Record<Role, Extent>> {
  try {
    const value: unknown = JSON.parse(storage.getItem(FIELD_WIDTH_KEY) ?? '{}');
    return value && typeof value === 'object' ? value as Partial<Record<Role, Extent>> : {};
  } catch {
    return {};
  }
}

export function writeFieldWidth(
  storage: ManualTasteStorage, role: Role, extent: Extent,
): Partial<Record<Role, Extent>> {
  const widths = { ...readFieldWidths(storage), [role]: extent };
  try { storage.setItem(FIELD_WIDTH_KEY, JSON.stringify(widths)); } catch { /* private mode */ }
  return widths;
}

export const MANUAL_TASTE_KEY = 'morph.demo.manual-taste.v1';
const LEARNED_EVIDENCE = 5.99;

export interface ManualTasteStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface ManualTasteController {
  readonly ledger: readonly SignalEvent[];
  taste(now?: number): TastePriors;
  hide(role: Role, now?: number): SignalEvent;
  promote(role: Role, now?: number): SignalEvent;
  correct(role: Role, knob?: 'role.presence', value?: string, now?: number): void;
}

function read(storage: ManualTasteStorage): SignalEvent[] {
  try {
    const value: unknown = JSON.parse(storage.getItem(MANUAL_TASTE_KEY) ?? '[]');
    return Array.isArray(value) ? value as SignalEvent[] : [];
  } catch {
    return [];
  }
}

/**
 * The demo's discovery channel. Literal field visibility remains presentation
 * state; only repeated role-presence observations enter this persisted ledger.
 */
export function createManualTaste(
  storage: ManualTasteStorage,
  sessionId: string = crypto.randomUUID(),
): ManualTasteController {
  let ledger = read(storage);
  const persist = () => {
    try { storage.setItem(MANUAL_TASTE_KEY, JSON.stringify(ledger)); } catch { /* private mode */ }
  };

  const taste = (now = Date.now()): TastePriors => {
    const claims = fold(ledger, { now });
    const suppressedRoles: Role[] = [];
    const prominentRoles: Role[] = [];
    for (const claim of claims.values()) {
      if (claim.knob === 'role.presence' && claim.index
        && claim.evidence >= LEARNED_EVIDENCE) {
        /* Decision 0038. Uniqueness lives at the shared inference boundary now,
           not in a resolver local to this file: `uniqueArgmax` abstains on a
           tie rather than manufacturing a winner from domain order, and
           `undefined` means "no learned override" rather than a neutral value
           every knob is assumed to share. The majority guard stays beside it —
           0038 keeps evidence, majority, compileability and uniqueness as four
           independent gates, and passing one does not waive another. */
        const presence = uniqueArgmax(claim.posterior);
        if (presence === undefined || claim.posterior[presence]! <= 0.5) continue;
        if (presence === 'suppressed') suppressedRoles.push(claim.index as Role);
        /* A promoted media/title/subtitle compiles to nothing — `rankRoles`
           gives them their structural weight before it looks at
           `prominentRoles`. Reporting the belief anyway would tell a person
           Morph had learned something and then change nothing, which is the
           defect decision 0036 withdrew `role.extent` for. Filtered here as
           well as at the control, so ledger evidence written before the
           control was hidden stays inert rather than becoming a false claim. */
        if (presence === 'prominent' && promotionCanCompile(claim.index as Role)) {
          prominentRoles.push(claim.index as Role);
        }
      }
    }
    return {
      ...(suppressedRoles.length ? { suppressedRoles } : {}),
      ...(prominentRoles.length ? { prominentRoles } : {}),
    };
  };

  return {
    get ledger() { return ledger; },
    taste,
    hide(role, now = Date.now()) {
      const event: SignalEvent = {
        id: crypto.randomUUID(),
        at: now, sessionId, class: 'adjust', scope: { level: 'global' },
        observations: [{ knob: 'role.presence', index: role, value: 'suppressed' }],
      } as SignalEvent;
      ledger = [...ledger, event];
      persist();
      return event;
    },
    promote(role, now = Date.now()) {
      const event: SignalEvent = {
        id: crypto.randomUUID(),
        at: now, sessionId, class: 'adjust', scope: { level: 'global' },
        observations: [{ knob: 'role.presence', index: role, value: 'prominent' }],
      } as SignalEvent;
      ledger = [...ledger, event];
      persist();
      return event;
    },
    /**
     * Retract the evidence for ONE knob and ONE value.
     *
     * @Codex, reviewing the first shape: *"correcting `roomy` also destroys
     * prior `compact` or `hug` evidence instead of allowing the remaining
     * evidence to become the fallback belief."* The value is now required
     * rather than inferred, because a knob with three learnable values cannot
     * be corrected by naming the knob alone — and `role.presence` has two.
     */
    correct(role, knob = 'role.presence', value = 'suppressed', now = Date.now()) {
      for (const event of ledger) {
        if (!event.retractedAt && event.observations.some((observation) =>
          observation.knob === knob
            && observation.index === role
            && observation.value === value)) {
          ledger = retract(ledger, event.id, now);
        }
      }
      persist();
    },
  };
}

/**
 * Apply ONE role's newly folded presence, leaving every other role alone.
 *
 * The handlers used to remove the acted-on role from whichever array it had
 * just left, which is only half the move. @Codex, on the correction path:
 * *"after three hides and seven promotions, correcting `prominent` makes
 * `manualTaste.taste()` resolve the role as suppressed, while the live state
 * shows it as normal until a reload rehydrates the ledger and unexpectedly
 * hides it."* Retracting the winner can immediately expose the loser as the
 * new winner, and nothing was applying it.
 *
 * Per-role rather than a spread of the whole fold, because `state.taste` also
 * holds preferences accepted from a SUGGESTION — `CANDIDATES` carries
 * `suppressedRoles: ['duration']` — which the manual ledger has never heard of
 * and must not drop. One gesture changes evidence for one role, so applying
 * one role is the whole of the correct move.
 */
export function applyPresence(
  taste: TastePriors, role: Role, learned: TastePriors,
): TastePriors {
  const put = (current: readonly Role[] | undefined, wanted: boolean): Role[] => {
    const others = (current ?? []).filter((item) => item !== role);
    return wanted ? [...others, role] : others;
  };
  return {
    ...taste,
    suppressedRoles: put(taste.suppressedRoles, !!learned.suppressedRoles?.includes(role)),
    prominentRoles: put(taste.prominentRoles, !!learned.prominentRoles?.includes(role)),
  };
}

